import { Component, type ErrorInfo, type ReactNode } from 'react'
import './ErrorBoundary.css'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Top-level error boundary.
 *
 * Catches uncaught render-phase errors anywhere in the React tree and shows
 * a graceful fallback with a reload action, instead of letting React unmount
 * the tree and leaving the user with a blank page (the iOS-Safari
 * `Notification` ReferenceError on the deployed dashboard is the prototype
 * for why this exists). The original error is rethrown to the host so it
 * still surfaces in error-monitoring + the console, just without taking
 * down the whole UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to the console so the error still surfaces in DevTools. Any
    // monitoring integration (Sentry/Bugsnag/etc.) can hook in here later.
    console.error('Uncaught render error:', error, errorInfo)
  }

  handleReload = (): void => {
    window.location.reload()
  }

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children
    }

    return (
      <div className="error-boundary-fallback" role="alert">
        <div className="error-boundary-card">
          <h1>Something went wrong</h1>
          <p>
            The dashboard hit an unexpected error and couldn't render. Reloading
            usually clears transient issues; if it persists, check the browser
            console or contact the operator.
          </p>
          {import.meta.env.DEV && this.state.error && (
            <pre className="error-boundary-details">
              {this.state.error.name}: {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
          )}
          <button
            type="button"
            className="error-boundary-reload"
            onClick={this.handleReload}
          >
            Reload
          </button>
        </div>
      </div>
    )
  }
}
